import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { 
  Database, Search, Terminal, AlertTriangle, Filter, X, 
  ChevronRight, Layers, Cpu, Code, Tags 
} from 'lucide-react'; 
import Pagination from '../components/Pagination';
import { AuthContext } from '../../context/AuthContext';
import ProductCard from '../components/ProductCard';

// Helper to get params
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Product = () => {
  const { loadingHandle } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();

  // --- DATA STATES ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mainTechs, setMainTechs] = useState([]);
  const [techStackTags, setTechStackTags] = useState([]);

  // --- FILTER STATES ---
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  
  // Single source of truth for current filters
  const [filters, setFilters] = useState({
    search: query.get('search') || '',
    category: query.get('category') || '',
    main_tech: query.get('main_tech') || '',
    tech_stack: query.get('tech_stack') || ''
  });

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(12);

  // --- 1. INITIAL FETCH (Filter Options) ---
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [catRes, mainTechRes, tagsRes] = await Promise.all([
          axiosInstance.get('/categories'),
          axiosInstance.get('/main-tech'),
          axiosInstance.get('/products/tech-stacks')
        ]);
        setCategories(catRes.data);
        setMainTechs(mainTechRes.data);
        setTechStackTags(tagsRes.data);
      } catch (err) {
        console.error("Failed to load filters", err);
      }
    };
    fetchFilterOptions();
  }, []);

  // --- 2. FETCH PRODUCTS (Triggered by URL Change) ---
  useEffect(() => {
    const fetchProducts = async () => {
      loadingHandle(true);
      try {
        const params = {
          search: query.get('search'),
          category: query.get('category'),
          main_tech: query.get('main_tech'),
          tech_stack: query.get('tech_stack')
        };

        // Sync local state with URL
        setFilters(prev => ({
            ...prev,
            search: params.search || '',
            category: params.category || '',
            main_tech: params.main_tech || '',
            tech_stack: params.tech_stack || ''
        }));

        const { data } = await axiosInstance.get(`/products`, { params });
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products', error);
      } finally {
        loadingHandle(false);
      }
    };

    fetchProducts();
    window.scrollTo(0, 0);
  }, [location.search]);

  // --- HANDLERS ---

  const updateURL = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.main_tech) params.set('main_tech', newFilters.main_tech);
    if (newFilters.tech_stack) params.set('tech_stack', newFilters.tech_stack);
    
    navigate(`?${params.toString()}`);
    setCurrentPage(1); 
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateURL({ ...filters, search: filters.search });
  };

  const handleFilterClick = (key, value) => {
    // Toggle logic: if clicking the same value, remove it
    const newValue = String(filters[key]) === String(value) ? '' : value; 
    updateURL({ ...filters, [key]: newValue });
  };

  // Remove a specific filter (Used by the "Selected Filters" section)
  const removeFilter = (key) => {
    const newFilters = { ...filters, [key]: '' };
    updateURL(newFilters);
  };

  const clearAllFilters = () => {
    setFilters({ search: '', category: '', main_tech: '', tech_stack: '' });
    navigate('/products');
  };

  // Helper to find name by ID for display
  const getCategoryName = (id) => categories.find(c => c.id == id)?.name || 'Category';
  const getMainTechName = (id) => mainTechs.find(t => t.id == id)?.name || 'Tech';

  // Pagination Logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = products.slice(indexOfFirstPost, indexOfLastPost);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Check if any filter is active
  const hasActiveFilters = filters.search || filters.category || filters.main_tech || filters.tech_stack;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 relative font-sans">
      
      {/* Background Grid */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(34,211,238,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0"></div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* --- MOBILE FILTER TOGGLE --- */}
        <button 
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className="lg:hidden w-full mb-6 flex items-center justify-center gap-2 bg-[#0f172a] border border-[#22d3ee]/30 p-3 text-[#22d3ee] font-bold uppercase tracking-widest text-sm"
        >
          <Filter size={16} /> {isMobileFilterOpen ? 'Close Filters' : 'Open Filters'}
        </button>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* --- SIDEBAR (Filters) --- */}
          <aside className={`lg:w-1/4 space-y-8 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`}>
            
            {/* Search */}
            <div className="bg-[#0f172a] border border-slate-800 p-6 shadow-lg relative overflow-hidden group">
               <div className="absolute top-0 left-0 w-1 h-full bg-[#22d3ee] opacity-0 group-hover:opacity-100 transition-opacity"></div>
               <h3 className="text-white font-bold uppercase tracking-widest mb-4 flex items-center gap-2 text-xs">
                 <Search size={14} className="text-[#22d3ee]"/> Search Database
               </h3>
               <form onSubmit={handleSearchSubmit}>
                 <input 
                   type="text" 
                   value={filters.search}
                   onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                   placeholder="Keywords..." 
                   className="w-full bg-[#020617] border border-slate-700 text-slate-300 text-sm p-3 focus:border-[#22d3ee] focus:outline-none transition-colors font-mono"
                 />
               </form>
            </div>

            {/* Categories */}
            <div className="bg-[#0f172a] border border-slate-800 p-6 shadow-lg">
                <h3 className="text-white font-bold uppercase tracking-widest mb-4 flex items-center gap-2 text-xs border-b border-slate-800 pb-2">
                  <Layers size={14} className="text-[#22d3ee]"/> Categories
                </h3>
                <ul className="space-y-2">
                  {categories.map(cat => (
                    <li key={cat.id}>
                      <button 
                        onClick={() => handleFilterClick('category', cat.id)}
                        className={`w-full text-left text-sm flex items-center justify-between group transition-colors ${Number(filters.category) === cat.id ? 'text-[#22d3ee] font-bold' : 'text-slate-400 hover:text-white'}`}
                      >
                        <span>{cat.name}</span>
                        {Number(filters.category) === cat.id && <ChevronRight size={14} />}
                      </button>
                    </li>
                  ))}
                </ul>
            </div>

            {/* Main Tech */}
            <div className="bg-[#0f172a] border border-slate-800 p-6 shadow-lg">
                <h3 className="text-white font-bold uppercase tracking-widest mb-4 flex items-center gap-2 text-xs border-b border-slate-800 pb-2">
                  <Cpu size={14} className="text-[#22d3ee]"/> Core Tech
                </h3>
                <div className="space-y-2">
                  {mainTechs.map(tech => (
                    <label key={tech.id} className="flex items-center space-x-3 cursor-pointer group">
                      <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${Number(filters.main_tech) === tech.id ? 'border-[#22d3ee] bg-[#22d3ee]/20' : 'border-slate-600 group-hover:border-slate-400'}`}>
                        {Number(filters.main_tech) === tech.id && <div className="w-2 h-2 bg-[#22d3ee]"></div>}
                      </div>
                      <input 
                        type="checkbox" 
                        className="hidden" 
                        checked={Number(filters.main_tech) === tech.id} 
                        onChange={() => handleFilterClick('main_tech', tech.id)} 
                      />
                      <span className={`text-sm ${Number(filters.main_tech) === tech.id ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>{tech.name}</span>
                    </label>
                  ))}
                </div>
            </div>

            {/* Tech Stack Tags */}
            <div className="bg-[#0f172a] border border-slate-800 p-6 shadow-lg">
                <h3 className="text-white font-bold uppercase tracking-widest mb-4 flex items-center gap-2 text-xs border-b border-slate-800 pb-2">
                  <Code size={14} className="text-[#22d3ee]"/> Stack Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {techStackTags.map((tag, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleFilterClick('tech_stack', tag)}
                      className={`text-[10px] uppercase font-bold px-2 py-1 border transition-all ${
                        filters.tech_stack === tag 
                        ? 'bg-[#22d3ee] text-[#020617] border-[#22d3ee]' 
                        : 'bg-transparent text-slate-500 border-slate-700 hover:border-[#22d3ee] hover:text-[#22d3ee]'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
            </div>

            {/* Reset */}
            <button 
              onClick={clearAllFilters}
              className="w-full py-3 text-xs uppercase font-bold tracking-widest text-red-400 border border-red-900/50 hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
            >
              <X size={14} /> Reset System
            </button>

          </aside>

          {/* --- MAIN CONTENT --- */}
          <main className="lg:w-3/4">
            
            {/* Header / Stats */}
            <div className="mb-6 border-b border-[#22d3ee]/20 pb-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
               <div>
                  <div className="flex items-center gap-2 text-[#22d3ee] font-mono text-xs mb-1">
                    <Terminal size={14} />
                    <span>SYSTEM_QUERY_STATUS: OK</span>
                  </div>
                  <h1 className="text-3xl font-bold text-white tracking-wide uppercase">
                    Template Library
                  </h1>
               </div>
               <div className="text-right">
                  <p className="text-slate-500 text-sm font-mono">
                    // DISPLAYING <span className="text-white font-bold">{currentPosts.length}</span> OF <span className="text-[#22d3ee]">{products.length}</span> MODULES
                  </p>
               </div>
            </div>

            {/* --- ACTIVE FILTER TAGS (Display & Remove) --- */}
            {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-3 mb-8 bg-[#0f172a]/50 p-4 border border-dashed border-slate-800 rounded-lg">
                    <span className="text-xs uppercase font-bold text-slate-500 flex items-center gap-2">
                        <Tags size={14} /> Active Filters:
                    </span>
                    
                    {/* Search Tag */}
                    {filters.search && (
                        <button onClick={() => removeFilter('search')} className="flex items-center gap-1 bg-[#22d3ee]/10 text-[#22d3ee] border border-[#22d3ee]/50 px-3 py-1 rounded-sm text-xs font-mono hover:bg-[#22d3ee]/20 transition-colors group">
                            Search: "{filters.search}" <X size={12} className="group-hover:text-white"/>
                        </button>
                    )}

                    {/* Category Tag */}
                    {filters.category && (
                        <button onClick={() => removeFilter('category')} className="flex items-center gap-1 bg-[#22d3ee]/10 text-[#22d3ee] border border-[#22d3ee]/50 px-3 py-1 rounded-sm text-xs font-mono hover:bg-[#22d3ee]/20 transition-colors group">
                            Cat: {getCategoryName(filters.category)} <X size={12} className="group-hover:text-white"/>
                        </button>
                    )}

                    {/* Main Tech Tag */}
                    {filters.main_tech && (
                        <button onClick={() => removeFilter('main_tech')} className="flex items-center gap-1 bg-[#22d3ee]/10 text-[#22d3ee] border border-[#22d3ee]/50 px-3 py-1 rounded-sm text-xs font-mono hover:bg-[#22d3ee]/20 transition-colors group">
                            Core: {getMainTechName(filters.main_tech)} <X size={12} className="group-hover:text-white"/>
                        </button>
                    )}

                    {/* Tech Stack Tag */}
                    {filters.tech_stack && (
                        <button onClick={() => removeFilter('tech_stack')} className="flex items-center gap-1 bg-[#22d3ee]/10 text-[#22d3ee] border border-[#22d3ee]/50 px-3 py-1 rounded-sm text-xs font-mono hover:bg-[#22d3ee]/20 transition-colors group">
                            Stack: {filters.tech_stack} <X size={12} className="group-hover:text-white"/>
                        </button>
                    )}

                    {/* Clear All Link */}
                    <button onClick={clearAllFilters} className="text-xs text-red-400 hover:text-red-300 underline underline-offset-4 ml-auto">
                        Clear All
                    </button>
                </div>
            )}

            {/* Products Grid */}
            {products.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentPosts.map((product) => (
                    <div key={product.id} className="bg-[#0f172a] border border-slate-800 hover:border-[#22d3ee]/50 transition-all duration-300 hover:-translate-y-1 shadow-lg group">
                        <ProductCard item={product}/>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="mt-12 pt-8 border-t border-slate-800 flex justify-center">
                    <Pagination
                      postsPerPage={postsPerPage}
                      totalPosts={products.length}
                      paginate={paginate}
                      currentPage={currentPage}
                    />
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="flex flex-col items-center justify-center h-96 border border-dashed border-slate-800 bg-[#0f172a]/30 rounded-lg">
                 <AlertTriangle className="text-[#22d3ee] w-12 h-12 mb-4 opacity-50" />
                 <h2 className="text-lg font-bold text-white font-mono">NO_DATA_FOUND</h2>
                 <p className="text-slate-500 text-sm mt-2">Adjust filter parameters to view results.</p>
              </div>
            )}
          </main>

        </div>
      </div>
    </div>
  );
};

export default Product;